import { 
  Ion,
  Camera,
  Rectangle,
  Viewer,
  WebMercatorProjection,
  UrlTemplateImageryProvider,
  ArcGisMapServerImageryProvider,
  Color,
  FeatureDetection,
  WebMapTileServiceImageryProvider,
  HorizontalOrigin,
  VerticalOrigin,
  PrimitiveCollection,
  Event,
  ScreenSpaceEventHandler,
  EventHelper,
  ScreenSpaceEventType,
  Math,
  Primitive,
  GeometryInstance,
  RectangleGeometry,
  EllipsoidSurfaceAppearance,
  Material,
  buildModuleUrl,
  Cartesian3,
  NearFarScalar,
  CallbackProperty,
  SceneTransforms,
  EllipsoidalOccluder,
  Ellipsoid,
  PolylineCollection,
  LabelCollection,
  PolylinePipeline,
  Cartesian2,
  DistanceDisplayCondition,
  DistanceDisplayConditionGeometryInstanceAttribute
} from 'cesium'
import { toRaw } from 'vue'
import { WindLayer } from './wind3d'
import { useColorMap, Factor } from './colorMap'
import {PolygonLayer, PolylineLayer, ModelLayer, BillbordLayer, PointLayer} from '@/lib/layer'
import { changeColor, createUUID, getSteps } from '@/utils/custom-utils'

export default class CustomCesium {

    constructor(id, options) {
        Ion.defaultAccessToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJkMmI2OThiNS00YjVmLTQ3N2EtOTA2OS05YWJlN2Y3ODk5NWQiLCJpZCI6NzM2NzcsImlhdCI6MTYzNzExMzA1OH0.G_Von8-FRl4D71JwYpu76OhmWh0ltOp7sYhVIaw1P2s"
        Camera.DEFAULT_VIEW_RECTANGLE = Rectangle.fromDegrees(90, -20, 110, 90)
        this.viewer = new Viewer(id, {
            selectionIndicator: false,
            geocoder: false, // 位置查找工具
            homeButton: false, // 视角返回初始位置
            sceneModePicker: false, // 选择视角的模式（球体、平铺、斜视平铺）
            baseLayerPicker: false, // 图层选择器（地形影像服务）
            navigationHelpButton: false, // 导航帮助(手势，鼠标)
            animation: false, // 左下角仪表盘（动画器件）
            timeline: false, // 底部时间线
            fullscreenButton: false, // 全屏
            vrButton: false, // VR
            skyAtmosphere: false,
            infoBox: false, // 点击事件右上角弹黑框
            mapProjection: new WebMercatorProjection(), // 设置为墨卡托投影
            orderIndependentTranslucency: false,
            contextOptions: {
              requestWebgl2: false,
              webgl: {
                alpha: true,
                depth: true,
                stencil: false,
                antialias: true,
                powerPreference: "high-performance",
                premultipliedAlpha: true,
                preserveDrawingBuffer: false,
                failIfMajorPerformanceCaveat: false,
              },
              allowTextureFilterAnisotropic: true,
            },
            msaaSamples: 4,
            //imageryProvider: new UrlTemplateImageryProvider({
            //  url: "http://cfdt.ninecosmos.cn/darkmap-retina/gali/{z}/{x}/{y}.png",
            //})
            imageryProvider: new ArcGisMapServerImageryProvider({
              url : 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer'
            })
        });

        // 海图layer
        this.seaLayer = null
        this.seaLayerStatus = false

        this.viewer.scene.skyBox.show = false; // 去掉星空背景
        this.viewer.scene.backgroundColor = new Color(0, 0, 0, 0); // 背景色设为透明
        // this.viewer.scene.highDynamicRange = true
        // this.viewer.scene.globe.enableLighting = true
        this.viewer.scene.fxaa = true;
        this.viewer.scene.postProcessStages.fxaa.enabled = true; // 关闭锯齿
        if (FeatureDetection.supportsImageRenderingPixelated()) {
          this.viewer.resolutionScale = window.devicePixelRatio;
        }

        this.viewer.scene.globe.depthTestAgainstTerrain = false; // 关闭高程遮挡
        this.viewer.scene.globe.showGroundAtmosphere = false; // 关闭大气遮罩层
        // this.viewer.scene.debugShowFramesPerSecond = true

        this.viewer.imageryLayers.addImageryProvider(new WebMapTileServiceImageryProvider({
            url: 'http://{s}.tianditu.gov.cn/ibo_w/wmts?service=wmts&request=GetTile&version=1.0.0&LAYER=ibo&tileMatrixSet=w&TileMatrix={TileMatrix}&TileRow={TileRow}&TileCol={TileCol}&style=default&format=tiles&tk=b5bc04094061c6f6a0c466eb9e6486f0',
            layer: 'tdtIboLayer',
            style: 'default',
            format: 'image/png',
            tileMatrixSetID: 'GoogleMapsCompatible',
            tileWidth: 256,
            tileHeight: 256,
            subdomains: ['t0','t1','t2','t3','t4','t5','t6','t7']
        }))

        this.viewer.imageryLayers.addImageryProvider(new WebMapTileServiceImageryProvider({
            url: 'http://{s}.tianditu.gov.cn/cia_w/wmts?service=wmts&request=GetTile&version=1.0.0&LAYER=cia&tileMatrixSet=w&TileMatrix={TileMatrix}&TileRow={TileRow}&TileCol={TileCol}&style=default&format=tiles&tk=b5bc04094061c6f6a0c466eb9e6486f0',
            layer: 'tdtCiaLayer',
            style: 'default',
            format: 'image/png',
            tileMatrixSetID: 'GoogleMapsCompatible',
            tileWidth: 256,
            tileHeight: 256,
            subdomains: ['t0','t1','t2','t3','t4','t5','t6','t7']
        }))
        
        this.clientWidthScale = options.clientWidthScale
        this.primitiveCollection = this.viewer.scene.primitives.add(new PrimitiveCollection())
        this.viewer.scene.primitives.lowerToBottom(this.primitiveCollection)

        this.horizontal = {
          center: HorizontalOrigin.CENTER,
          left: HorizontalOrigin.LEFT,
          right: HorizontalOrigin.RIGHT
        }

        this.vertical = {
          top: VerticalOrigin.TOP,
          center: VerticalOrigin.CENTER,
          bottom: VerticalOrigin.BOTTOM,
          baseline: VerticalOrigin.BASELINE
        }

        /**this.colors = {
          red: Cesium.Color.RED,
          chartreuse: Cesium.Color.CHARTREUSE,
          blue: Cesium.Color.BLUE,
          yellow: Cesium.Color.YELLOW,
          orange: Cesium.Color.ORANGE,
          deeppink: Cesium.Color.DEEPPINK,
          white: Cesium.Color.WHITE,
          black: Cesium.Color.BLACK,
          deepskyblue: Cesium.Color.DEEPSKYBLUE,
          orangered: Cesium.Color.ORANGERED
        }**/

        this.events = {
            load: this.viewer.scene.globe.tileLoadProgressEvent,
            moveend: this.viewer.camera.moveEnd,
            postRender: this.viewer.scene.postRender,
            mapChange: new Event(),
            mapChangeStart: new Event()
        }

        const handler = new ScreenSpaceEventHandler(this.viewer.scene.canvas);
        this.bindCustomEvent(handler);

        this.Practicle = null
        this.mapType = '3d'

        this.playEntityPositions = [] //用于演示船的运行轨迹
        this.playEntity = null // 运动中的实体
        // 创建point layer
        this.pointLayer = new PointLayer()
        this.pointLayer.addTo(this.primitiveCollection, 'point-layer')
        // 创建line layer
        this.polyLineLayer = new PolylineLayer()
        this.polyLineLayer.addTo(this.primitiveCollection, 'polyline-layer')

        this.pastLineLayer = new PolylineLayer()
        this.pastLineLayer.addTo(this.primitiveCollection, 'pastline-layer')

        this.billbordLayer = new BillbordLayer()
        this.billbordLayer.addTo(this.primitiveCollection, 'billbord-layer')

        this.eventHelper = new EventHelper();
        const RemoveCallback = this.eventHelper.add(
            this.viewer.scene.globe.tileLoadProgressEvent, (event) => {
                if (event === 0) {
                    // 删除事件监听
                    RemoveCallback();
                    this.addLngLatGrids(this.clientWidthScale); //经纬度
                    //this.addCountryBoundaries(this.clientWidthScale)
                    //this.addWater()
                    //this.addModel()
                }
            }
        )
        this.layers = {}
    }

    seaLayerControl(status) {
      this.seaLayerStatus = status
      if(this.seaLayerStatus) {
        this.addSeaLayer()
      }else {
        this.removeSeaLayer()
      }
    }

    addSeaLayer() {
      this.seaLayer = this.viewer.imageryLayers.addImageryProvider(new UrlTemplateImageryProvider({
          url: 'http://124.70.45.225:21610/openseamap/{z}/{x}/{y}.png',
          tileWidth: 256,
          tileHeight: 256
      }))
    }

    removeSeaLayer() {
      this.viewer.imageryLayers.remove(this.seaLayer)
    }

    addModel() {
      let modelLayer = new ModelLayer({hasPick: true})
      modelLayer.addTo(this.viewer.scene.primitives)
      modelLayer.add('model-layer', [116.37947798, 39.90639041, 0], {
        url: 'http://localhost:8088/boat.glb',
        scale: 1,
        heading: 0,
        pitch: 0,
        roll: 0
      })
    }

    mapChange() {
      this.mapType === '3d' ? this.viewer.scene.morphTo2D(3) : this.viewer.scene.morphTo3D(3)
      this.mapType = this.mapType === '3d' ? '2d' : '3d'
    }

    setMapType(value) {
      this.mapType = value
      if(this.mapType === '2d') {
        this.viewer.scene.morphTo2D(3)
      } else {
        this.viewer.scene.morphTo3D(3)
      }
    }

    bindCustomEvent(handler) {
        this.events.click = new Event()
        this.events.dbclick = new Event()
        this.events.mousemove = new Event()
        this.events.markerclick = new Event()
        let that = this
        this.viewer.scene.morphComplete.addEventListener(event => {
          this.events.mapChange.raiseEvent({type: this.mapType})
        })

        this.viewer.scene.morphStart.addEventListener(event => {
          this.events.mapChangeStart.raiseEvent()
        })
        handler.setInputAction((event) => {
            event.type = 'custom_MouseMove'
            event.lngLat = that.getLngLatByPosition(event.endPosition)
            that.events.mousemove.raiseEvent(event)
        }, ScreenSpaceEventType.MOUSE_MOVE)

        handler.setInputAction((event) => {
            event.type = 'custom_LeftClick'
            event.lngLat = that.getLngLatByPosition(event.position)
            that.events.click.raiseEvent(event)
        }, ScreenSpaceEventType.LEFT_CLICK)

        handler.setInputAction((event) => {
            event.type = 'custom_LeftDoubleClick'
            event.lngLat = that.getLngLatByPosition(event.position)
            that.events.dbclick.raiseEvent(event)
        }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK)
    }

    getLngLatByPosition(position) {
        if(position) {
            let cartesian  = this.viewer.camera.pickEllipsoid(
                position,
                this.viewer.scene.globe.ellipsoid
            )
            if(cartesian) {
                let cartographic = this.viewer.scene.globe.ellipsoid.cartesianToCartographic(cartesian)
                return {
                    lng: Math.toDegrees(cartographic.longitude),
                    lat: Math.toDegrees(cartographic.latitude)
                }
            }
        }
        return null
    }

    pick(position) {
        const pick = this.viewer.scene.pick(position);
        return pick
    }

    createColorMapPrimitive(options) {
        return useColorMap({
            url: options.url,
            factor: Factor[options.factor],
            mapType: this.mapType
        })
    }

    addWater() {
        const primitive = new Primitive({
          geometryInstances: new GeometryInstance({
            geometry: new RectangleGeometry({
              rectangle: Rectangle.fromDegrees(-180, -90, 180, 90),
              vertexFormat: EllipsoidSurfaceAppearance.VERTEX_FORMAT
            }),
            attributes: {
              distanceDisplayCondition: new DistanceDisplayConditionGeometryInstanceAttribute(0, 5000000)
            }
          }),
          appearance: new EllipsoidSurfaceAppearance({
            aboveGround: false,
            material: new Material({
              fabric: {
                type: 'Water',
                uniforms: {
                  specularMap: "./cesium/earthspec1k.jpg",
                  normalMap: buildModuleUrl('./cesium/waterNormals.jpg'),
                  baseWaterColor: Color.fromCssColorString('#00456b'),
                  frequency: 100000.0,
                  animationSpeed: 0.005,
                  amplitude: 15.0,
                  specularIntensity: 0.4
                }
              }
            })
          }),
          show: true
        })
        this.addPrimitive(primitive, 'water')
    }

    /**
     * 图层线
     * @param {Object} polyline 线对象
     * @param {Object} [polyline.positions]
     * @param {Boolean} [polyline.positions.hasHeight] 坐标是否包含高度
     * @param {Array<Number>} [polyline.positions.value]
     * @param {String} [polyline.type] 线类型
     * @param {Array | String} [polyline.color] 线颜色
     * @param {Number} [polyline.width] 线宽度
     * @param {Number} [polyline.dashLength] 虚线长度，type为dash时有效
     * @param {Array | String} [polyline.outlineColor] 外边框颜色
     * @param {Number} [polyline.outlineWidth] 外边框宽度
     * @param {Number} [polyline.glowPower] 亮度，type为glow时有效
     * @param {Number} [polyline.taperPower]
     * @param {String | Number} id ID
     * @returns 
     */
    addPolyline(polyline, id) {
      const lineId = id ? id : createUUID()
      const line = this.createPolyline(polyline)
      this.polyLineLayer.add(line, lineId)
      return lineId
    }
    addPoint(point) {
      point.id = point.id ? point.id : createUUID()
      this.pointLayer.add(point)
    }

    clearPoint() {
      this.pointLayer.clear()
    }

    addBillbord(billbord, id) {
      const billbordId = id ? id : createUUID()
      const options = Object.assign({
        id: billbordId,
        scale: 1,
        verticalOrigin: 'center',
        horizontalOrigin: 'center'
      }, billbord)

      const entity = {
        id: options.id,
        position: Cartesian3.fromDegrees(...options.position),
        image: options.image,
        scale: options.scale,
        verticalOrigin: this.vertical[options.verticalOrigin],
        horizontalOrigin: this.horizontal[options.horizontalOrigin]
      }
      if(options.scaleByDistance) {
        delete entity['scale']
        entity.scaleByDistance = new NearFarScalar(...options.scaleByDistance)
      }

      this.billbordLayer.add(entity, billbordId)
      return billbordId
    }

    removeBillbord(id) {
      this.billbordLayer.removeById(id)
    }

    addPastline(polyline, id) {
      const lineId = id ? id : createUUID()
      const line = this.createPolyline(polyline)
      this.pastLineLayer.add(line, lineId)
      return lineId
    }

    removePastline(id) {
      this.pastLineLayer.removeById(id)
    }

    /**
     * 创建Polyline对象
     * @param {Object} polyline 线对象
     * @param {Object} [polyline.positions]
     * @param {Boolean} [polyline.positions.hasHeight] 坐标是否包含高度
     * @param {Array<Number>} [polyline.positions.value]
     * @param {String} [polyline.type] 线类型
     * @param {Array | String} [polyline.color] 线颜色
     * @param {Number} [polyline.width] 线宽度
     * @param {Number} [polyline.dashLength] 虚线长度，type为dash时有效
     * @param {Array | String} [polyline.outlineColor] 外边框颜色
     * @param {Number} [polyline.outlineWidth] 外边框宽度
     * @param {Number} [polyline.glowPower] 亮度，type为glow时有效
     * @param {Number} [polyline.taperPower]
     */
    createPolyline(polyline) {
      const options = Object.assign({
        color: 'white',
        width: 1,
        dashLength: 10,
        outlineColor: 'white',
        outlineWidth: 1,
        type: 'outline'
      }, polyline)

      let material = null
      if(options.type === 'outline') {
        material = Material.fromType(Material.PolylineOutlineType, {
          color: changeColor(options.color),
          outlineColor: changeColor(options.outlineColor),
          outlineWidth: options.outlineWidth,
        })
      }else if(options.type === 'dash') {
        material = Material.fromType(Material.PolylineDashType, {
          color: changeColor(options.color),
          dashLength: options.dashLength,
          outlineColor: changeColor(options.outlineColor),
          outlineWidth: options.outlineWidth,
        })
      }else if(options.type === 'arrow') {
        material = Material.fromType(Material.PolylineArrowType, {
          color: changeColor(options.color)
        })
      }else if(options.type === 'glow') {
        material = Material.fromType(Material.PolylineGlowType, {
          color: changeColor(options.color),
          glowPower: options.glowPower,
          taperPower: options.taperPower
        })
      }else {
        throw new Error('line type ['.concat(options.type).concat('] non-existent'))
      }

      let positions = null
      if(options.positions) {
        if(options.positions.hasHeight) {
          positions = Cartesian3.fromDegreesArrayHeights(polyline.positions.value)
        }else{
          positions = Cartesian3.fromDegreesArray(polyline.positions.value)
        }
      }else{
        throw new Error('line points is empty')
      }
      return {
        positions: positions,
        width : options.width,
        material: material
      }
    }

    removePolyline(id) {
      this.polyLineLayer.removeById(id)
    }

    addPrimitive(primitive, id) {
        if(!id) {
          id = createUUID()
        }
        let metadata = {id : id}
        primitive.metadata = metadata
        let p = this.viewer.scene.primitives.add(primitive)
        if(id === 'weather-primitive') {
            this.viewer.scene.primitives.lowerToBottom(p)
        }
        return id;
    }

    getPrimitiveById(id) {
        let primitives = this.viewer.scene.primitives;
          for(let i = 0; i< primitives.length; i++) {
              let primitive = primitives.get(i)
              if(primitive && primitive.metadata && primitive.metadata.id === id) {
                  return primitive
              }
          }
    }

    /**
     * 根据图层ID删除图层
     * @param {String} id 图层ID
     */
    removePrimitive(id) {
        let primitives = this.viewer.scene.primitives;
        for(let i = 0; i< primitives.length; i++) {
            let primitive = primitives.get(i)
            if(primitive && primitive.metadata&& primitive.metadata.id === id) {
                primitives.remove(primitive)
            }
        }
    }

    /**
     * 粒子效果
     * @param {Object} options 
     */
     addCustomPracticle(data, options) {
        if(this.mapType === '2d') {
          return
        }
        const v = toRaw(this.viewer) // toRaw之后proxy对象更新不会触发视图更新
        const windOptions = {
            maxAge: options.maxAge,
            lineWidth: options.lineWidth, // 绘制粒子的线宽
            globalAlpha: options.globalAlpha, // 透明度
            velocityScale: options.velocityScale, // 风速的比例 ( 粒子的小尾巴长度 )
            frameRate: options.frameRate, // 每秒所需的帧数
            paths: this.mapType === '2d' ? Math.floor(options.paths/3) : options.paths,
            mapType: this.mapType
        }
        let practicles = null;
        practicles = new WindLayer(data, windOptions)
        practicles.addTo(v)
        this.Practicle = practicles
    }

    removeCustomPracticle() {
        if(this.Practicle) {
            this.Practicle.remove()
            this.Practicle = null
        }
    }

    changePrimitiveMaterial(id, data) {
        let primitive = this.getPrimitiveById(id)
        primitive.appearance.material.uniforms.image = data
    }

    addEntity(entity, id) {
        const entityId = id ? id : createUUID()
        entity.id = entityId
        if(entity.billbord && entity.billbord.rotation) {
          entity.orientation = {
            heading: entity.billbord.rotation,
            pitch: 0,
            roll: 0
          }
        }
        this.viewer.entities.add(entity)
        return entityId
    }

    addLayer(layer) {
      layer.addTo(this.viewer.scene.primitives)
    }

    removeLayer(layerId) {
      
    }

    removeEntity(id) {
        this.viewer.entities.removeById(id)
    }

    setEntityOptions(id, options) {
      this.playEntity = this.viewer.entities.getById(id)
      if(this.playEntity) {
        this.playEntity.billboard.rotation = options.rotation
        if(options.isPlay) {
          const positions = getSteps(options.start, options.end, 15)
          this.playEntityPositions.push(...positions)
        }else{
          this.playEntity.position = new CallbackProperty(() => {
          return Cartesian3.fromDegrees(...options.end)
          }, false)
        }
      }
    }

    setTimer() {
      this.timer = setInterval(this.playEntityAnamition.bind(this), 100)
    }

    playEntityAnamition() {
      const position = this.playEntityPositions.shift()
      if(position && this.playEntity) {
        this.playEntity.position = new CallbackProperty(() => {
          return position
        }, false)
      }
    }

    clearTimer() {
      if(this.timer) {
        clearInterval(this.timer)
        //this.viewer.entities.remove(this.playEntity)
        //this.playEntity = null
        this.playEntityPositions = []
      }
    }

    flyTo(point , height = 0) {
        if(height === 0) {
            const ellipsoid=this.viewer.scene.globe.ellipsoid;
            const cartesian3 = this.viewer.camera.position;
            const cartographic = ellipsoid.cartesianToCartographic(cartesian3);
            height = cartographic.height
          }
    
          this.viewer.camera.flyTo({
            destination: Cartesian3.fromDegrees(Number(point.lon), Number(point.lat), height),
            duration: 2
        })
    }

    pointToScreen(point) {
        return SceneTransforms.wgs84ToWindowCoordinates(this.viewer.scene, Cartesian3.fromDegrees(...point))
    }

    pointIsShow(point) {
        if(this.mapType === '2d') {
          return true
        }
        const status = new EllipsoidalOccluder(
          Ellipsoid.WGS84,
          this.viewer.camera.position
        ).isPointVisible(
          Cartesian3.fromDegrees(...point)
        );
        return status
    }

    /**
     * 添加经纬度网格
     * @param {number} clientWidthScale 
     */
     addLngLatGrids(clientWidthScale) {
        const color = new Color.fromBytes(153, 236, 255, 50); // 线条颜色
        const width = clientWidthScale / 2;
        const scale = clientWidthScale / 7;
        let polylines = this.viewer.scene.primitives.add(new PolylineCollection())
        let labels = this.viewer.scene.primitives.add(new LabelCollection())
        // 经度
        for (let i = -180; i < 180; i = i + 20) {
          const xpoints = [];
          const coord = [i, -90, 0, i, 89, 0];
          xpoints.push(...coord);
          // 加经度线条
          polylines.add({
            positions: PolylinePipeline.generateCartesianArc({
              positions: Cartesian3.fromDegreesArrayHeights(xpoints)
            }),
            width: width,
            material: Material.fromType(Material.PolylineOutlineType, {
              color: color,
              outlineColor: Color.BLACK.withAlpha(0.1),
              outlineWidth: 0,
            }),
          });
          // 加经度标签
          let label = "";
          if (i > 0) {
            label = i + "°E";
          } else if (i < 0) {
            label = i * -1 + "°W";
          } else {
            label = i + "°";
          }
          const labelCoord = [i, 0, 0];
          labels.add({
            position: Cartesian3.fromDegrees(...labelCoord),
            text: label,
            font: "48px sans-serif",
            scale: scale,
            fillColor: Color.WHITE.withAlpha(0.7),
            outlineColor: Color.WHITE.withAlpha(0.7),
            outlineWidth: 1,
            pixelOffset: new Cartesian2(5, 20),
          });
        }
  
        // 纬度
        for (let j = -60; j <= 60; j = j + 15) {
          const ypoints = [];
          for (let i = 0; i <= 180; i = i + 1) {
            const coord = [i, j, 0];
            ypoints.push(...coord);
          }
          for (let i = -179; i <= 0; i = i + 1) {
            const coord = [i, j, 0];
            ypoints.push(...coord);
          }
          // 加纬度线条
          polylines.add({
            positions: PolylinePipeline.generateCartesianArc({
              positions: Cartesian3.fromDegreesArrayHeights(ypoints)
            }),
            width: width,
            material: Material.fromType(Material.PolylineOutlineType, {
              color: color,
              outlineColor: Color.BLACK.withAlpha(0.1),
              outlineWidth: 0,
            }),
            
          });
          // 加纬度标签
          let label = "";
          if (j > 0) {
            label = j + "°N";
          } else if (j < 0) {
            label = j * -1 + "°S";
          } else {
            label = j + "°";
          }
          const labelCoord = [180, j, 0];
          labels.add({
            position: Cartesian3.fromDegrees(...labelCoord),
            text: label,
            font: "48px sans-serif",
            scale: scale,
            fillColor: Color.WHITE.withAlpha(0.7),
            outlineColor: Color.WHITE.withAlpha(0.7),
            outlineWidth: 1,
            pixelOffset: new Cartesian2(-15, 0)
          });
        }
    }

    addCountryBoundaries(clientWidthScale) {
      let polygonLayer = new PolygonLayer('polygon-layer', {
        fillColor: [255,0,255,200],
        hasClick: true,
        showOutline: false,
        showFill: true,
        outlineColor: '#FFFF00',
        outlineWidth: 1
      })

      polygonLayer.addTo(this.viewer.scene.primitives)
      polygonLayer.addGeoJSON('./worldarea.json')
    }
    /**
     * 
     * @param {*} layerId 图层ID
     * @param {*} features  要素几何
     * @param {*} color 颜色
     * @param {*} alpha 透明度
     */
    addPolygonList(layerId, features, color, alpha = 0.4) {
      const polygonLayer = new PolygonLayer(layerId, {
        fillColor: color,
        hasClick: false,
        alpha: alpha,
        showOutline: true,
        showFill: true,
        outlineColor: color,
        outlineWidth: 1
      })

      polygonLayer.addTo(this.viewer.scene.primitives)
      polygonLayer.addGeoJSON(features)
    }

    /**
     * 
     * @param {*} layerId 图层ID
     * @param {*} color 颜色
     * @param {*} alpha 透明度
     * @return layer
     */
    addPolygonLayer(id, color, alpha = 0.4) {
      const polygonLayer = new PolygonLayer(id, {
        fillColor: color,
        hasClick: false,
        alpha: alpha,
        showOutline: true,
        showFill: true,
        outlineColor: color,
        outlineWidth: 1
      })

      polygonLayer.addTo(this.viewer.scene.primitives)
      this.layers[String(id)] = polygonLayer
    }

    setDataByLayer(layerId, geojson) {
      const layer = this.layers[String(layerId)]
      if (layer && layer.getType() === 'PolygonLayer') {
        layer.setData(geojson)
      }
    }

    clearLayers() {
      const keys = Object.keys(this.layers)
      keys.map(item => {
        this.layers[item].clear()
      })
    }

    clearLayer(layerId) {
      if (this.layers[layerId]) {
        this.layers[layerId].clear()
      }
    }

    addCountryLabels(clientWidthScale) {
        const countries = [
            { lon: 104.2, lat: 35.86, name: "中国" },
            { lon: 105.32, lat: 61.52, name: "俄罗斯" },
            { lon: -3.44, lat: 55.38, name: "英国" },
            { lon: 78.96, lat: 20.59, name: "印度" },
            { lon: 2.21, lat: 46.23, name: "法国" },
            { lon: -95.71, lat: 37.09, name: "美国" },
            { lon: 138.25, lat: 36.2, name: "日本" },
            { lon: 6.13, lat: 49.82, name: "卢森堡" },
            { lon: -106.35, lat: 56.13, name: "加拿大" },
            { lon: 10.45, lat: 51.17, name: "德国" },
            { lon: -51.93, lat: -14.24, name: "巴西" },
            { lon: 127.98, lat: 37.66, name: "韩国" },
            { lon: 45.08, lat: 23.89, name: "沙特阿拉伯" },
            { lon: 12.57, lat: 41.87, name: "意大利" },
            { lon: 53.85, lat: 23.42, name: "阿拉伯联合酋长国" },
            { lon: 133.78, lat: -25.27, name: "澳大利亚" },
            { lon: 9.5, lat: 56.26, name: "丹麦" },
            { lon: 34.85, lat: 31.05, name: "以色列" },
            { lon: 69.35, lat: 30.38, name: "巴基斯坦" },
            { lon: 35.24, lat: 38.96, name: "土耳其" },
            { lon: 103.82, lat: 1.35, name: "新加坡" },
            { lon: -3.75, lat: 40.46, name: "西班牙" },
            { lon: 113.92, lat: -0.79, name: "印度尼西亚" },
            { lon: 66.92, lat: 48.02, name: "哈萨克斯坦" },
            { lon: 25.75, lat: 61.92, name: "芬兰" },
            { lon: -63.62, lat: -38.42, name: "阿根廷共和国" },
            { lon: 21.82, lat: 39.07, name: "希腊" },
            { lon: 101.98, lat: 4.21, name: "马来西亚" },
            { lon: 8.68, lat: 9.08, name: "尼日利亚" },
            { lon: 27.95, lat: 53.71, name: "白俄罗斯" },
            { lon: -102.55, lat: 23.63, name: "墨西哥" },
            { lon: 47.58, lat: 40.14, name: "阿塞拜疆共和国" },
            { lon: -78.18, lat: -1.83, name: "厄瓜多尔" },
            { lon: 19.15, lat: 51.92, name: "波兰" },
            { lon: 121.77, lat: 12.88, name: "菲律宾" },
            { lon: 8.23, lat: 46.82, name: "瑞士" },
            { lon: -71.54, lat: -35.68, name: "智利" },
            { lon: 25.01, lat: 58.6, name: "爱沙尼亚" },
            { lon: 22.94, lat: -30.56, name: "南非" },
            { lon: -63.59, lat: -16.29, name: "玻利维亚" },
            { lon: -75.02, lat: -9.19, name: "秘鲁" },
            { lon: 90.36, lat: 23.68, name: "孟加拉" },
            { lon: 30.8, lat: 26.82, name: "埃及" },
        ]
        const scale = clientWidthScale / 6;
        let labels = new LabelCollection()
        //let labels = this.viewer.scene.primitives.add(new LabelCollection())
        
        countries.forEach((item, i) => {
          if(item.lon && item.lat) {
              labels.add({
                  position: Cartesian3.fromDegrees(item.lon, item.lat, 20000),
                  id: i,
                  text: item.name,
                  font: "48px sans-serif",
                  scale: scale,
                  fillColor: Color.WHITE,
                  outlineColor: Color.WHITE,
                  outlineWidth: 1,
                  clampToGround: true,
                  distanceDisplayCondition: new DistanceDisplayCondition(0, 17540000),
              })
          }
        })
       this.viewer.scene.primitives.add(labels)
    }

    on(event, callback) {
        this.eventHelper.add(this.events[event], (event, time) => {
            if(event === 0 || !event || event.type || time) {
                callback(event)
            }
        })
    }

    once(event, callback) {
        const removeEvent = this.eventHelper.add(this.events[event], (event) => {
            if(event === 0 || !event || event.type) {
                removeEvent()
                callback(event)
            }
        })
    }

    getViewer() {
        return this.viewer
    }

    getContainer() {
        return this.viewer.cesiumWidget.container
    }

    /**
     * 
     * @param {Array<Number> | String} color 
     */
     /**changeColor(color) {
      if(!color) {
        throw new Error('颜色不能为空！')
      }
      if(typeof color === 'string') {
        if(color.startsWith('#')) {
          return Cesium.Color.fromCssColorString(color)
        }else {
          return this.colors[color]
        }
      }else if(Array.isArray(color)) {
        if(color.length > 3) {
          return Cesium.Color.fromBytes(color[0], color[1], color[2], color[3])
        }else if(color.length == 3) {
          return Cesium.Color.fromBytes(color[0], color[1], color[2], 255)
        }else{
          throw new Error('数组格式颜色不能低于3个！')
        }
      }else{
        throw new Error('颜色格式不正确！')
      }
    }**/

    /**生成UUID 
    createUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          var r = Math.random() * 16 | 0,
              v = c == 'x' ? r : (r & 0x3 | 0x8)
          return v.toString(16)
        })
    }*/
}